/* Uses a native Shopify product-form POST; no theme cart-drawer dependency. */
(() => {
  if (customElements.get('mpp-paper-voucher')) return;

  class PaperVoucher extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form = this.querySelector('form');
      this.amount = this.querySelector('[data-voucher-amount]');
      this.variantInput = this.querySelector('[name="id"]');
      this.valueProperty = this.querySelector('[data-voucher-value]');
      this.button = this.querySelector('[data-voucher-submit]');
      this.error = this.querySelector('[data-voucher-error]');
      this.price = this.querySelector('[data-voucher-price]');
      if (!this.form || !this.amount) return;
      this.config = JSON.parse(this.querySelector('[data-voucher-config]').textContent);
      this.submitting = false;
      this.messages = this.config.messages;
      this.variants = new Map();
      this.duplicates = new Set();
      for (const variant of this.config.variants) {
        const raw = String(variant.amount).trim();
        if (!/^\d+$/.test(raw)) continue;
        const amount = Number(raw);
        if (this.variants.has(amount)) this.duplicates.add(amount);
        this.variants.set(amount, variant);
      }
      const country = this.querySelector('[data-voucher-country]');
      if (!country.dataset.initialized) {
        country.value = this.config.defaultCountry;
        if (!country.value) country.selectedIndex = 0;
        country.dataset.initialized = 'true';
      }

      const requestedId = new URL(location.href).searchParams.get('variant');
      const requested = this.config.variants.find(v => String(v.id) === requestedId);
      if (!this.amount.value) this.amount.value = requested?.amount ?? this.config.defaultAmount;

      this.amount.addEventListener('input', () => this.update(true), { signal });
      this.amount.addEventListener('change', () => this.update(true), { signal });
      this.form.addEventListener('submit', event => this.submit(event), { capture: true, signal });
      this.form.addEventListener('input', event => {
        if (event.target.matches('input[type="text"][required]')) {
          event.target.setCustomValidity(event.target.value.trim() ? '' : this.messages.required);
        }
      }, { signal });
      window.addEventListener('pageshow', () => {
        this.submitting = false;
        this.form.removeAttribute('aria-busy');
        this.update(false);
      }, { signal });
      this.update(true);

      if (window.Shopify?.designMode) {
        const missing = [];
        for (let n = 50; n <= 1000; n += 10) if (!this.variants.has(n)) missing.push(n);
        const diagnostic = this.querySelector('[data-voucher-diagnostic]');
        if (diagnostic && missing.length) {
          diagnostic.hidden = false;
          diagnostic.textContent = `Missing amount variants (CHF): ${missing.join(', ')}. The full range requires 96 variants.`;
        }
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    selection() {
      if (this.config.giftCard || !this.config.validOptions) return { error: this.messages.configuration };
      if (this.config.currency !== 'CHF') return { error: this.messages.currency };
      const raw = this.amount.value.trim();
      const amount = Number(raw);
      if (!/^\d+$/.test(raw) || amount < 50 || amount > 1000 || amount % 10 !== 0) {
        return { error: this.messages.range };
      }
      const variant = this.variants.get(amount);
      if (!variant || this.duplicates.has(amount)) return { error: this.messages.missing };
      if (!variant.available) return { error: this.messages.unavailable };
      // The option is the face value; the configured Shopify price is authoritative.
      if (Number(variant.price) !== amount * 100) return { error: this.messages.price };
      return { amount, variant };
    }

    update(announce) {
      const result = this.selection();
      this.amount.setCustomValidity(result.error || '');
      this.amount.setAttribute('aria-invalid', result.error ? 'true' : 'false');
      this.error.textContent = result.error || '';
      this.error.hidden = !result.error;
      this.variantInput.value = result.variant ? String(result.variant.id) : '';
      this.valueProperty.value = result.variant ? `${result.amount} CHF` : '';
      this.price.textContent = result.variant ? `CHF ${result.amount.toLocaleString('de-CH')}` : '';
      this.button.disabled = Boolean(result.error) || this.submitting;
      if (announce && result.variant) {
        // Existing MPP listens to this change to refresh variant-specific media.
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return result;
    }

    submit(event) {
      // Own the submission so generic bubbling theme handlers cannot omit properties.
      event.stopImmediatePropagation();
      if (this.submitting) { event.preventDefault(); return; }
      const result = this.update(false);
      this.form.querySelectorAll('input[type="text"]').forEach(input => {
        input.value = input.value.trim();
        if (input.required) input.setCustomValidity(input.value ? '' : this.messages.required);
      });
      if (result.error || !this.form.reportValidity()) {
        event.preventDefault();
        if (result.error) this.amount.focus();
        return;
      }
      // One variant at quantity 1 = one voucher. Let the browser POST the whole form.
      this.submitting = true;
      this.form.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
    }
  }
  customElements.define('mpp-paper-voucher', PaperVoucher);
})();
